import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { ContainerContext, withContainerContext } from "../../components/container/ContainerContext";
import EmptyState from "../../components/emptyState";
import FormButton from "../../components/form/FormButton";
import SplitButton from "../../components/form/SplitButton";
import Icon from "../../components/interface/Icon";
import ItemCardListView from "../../components/itemCardListView/ItemCardListView";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { GLOBAL_FILTER_ID } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import { ITEM_CARD_LIST_SEQUENCE_HEADER_TITLE } from "../../constants/itemCardList";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import batchUtils from "../../modules/batchUtils";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { typeSelectors } from "../../modules/definition/types";
import dropSelectors from "../../modules/dropSelectors";
import { filterSelectors } from "../../modules/filters";
import { itemSelectors } from "../../modules/items";
import { timelineReducer } from "../../modules/timelineReducer";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import preprocessorQuery from "../../utils/preprocessorQuery";
import spreadsheetSelectors from "../spreadsheetView/selectors";
import { withWorkspaceContext } from "../workspace/WorkspaceContext";
import ItemCardListHeader from "./ItemCardListHeader";
import { getItemCardListContainerContextValue } from "./utils";

// import debugUtils from "../../utils/debugUtils";

const getViewData = (state, viewId, containerWith, showDragHandles) => {
  return spreadsheetSelectors.getItemCardListViewData(state, viewId, containerWith, showDragHandles);
};

const isReadonly = preprocessorQuery.isReadonly();

class MobileSpreadsheetView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.state = {
      data: immutableRecords.ItemCardListDisplayRecord,
      draggedItemIds: List()
    };

    this.dispatchLocal = this.dispatchLocal.bind(this);
    this.startRowDrag = this.startRowDrag.bind(this);
    this.onMoveRow = this.onMoveRow.bind(this);
    this.dispatchLastMoveAction = this.dispatchLastMoveAction.bind(this);
    this.requestCanDropItems = this.requestCanDropItems.bind(this);
    this.onCanItemDropOnRow = this.onCanItemDropOnRow.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.onToggleChildren = this.onToggleChildren.bind(this);
    this.onViewClick = this.onViewClick.bind(this);
    this.canTypeHaveSuperType = this.canTypeHaveSuperType.bind(this);
    this.requestViewBoundsForCardView = this.requestViewBoundsForCardView.bind(this);
  }

  componentWillMount() {
    this.copyLocalStore(this.props);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
    this.props.workViewRegistration.registerWorkViewCreateItemCallback(this.props.viewId, this.onCreateItem);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("MobileSpreadsheetView", this.props, nextProps, true);
    this.copyLocalStore(nextProps);
    if (this.headerRef && this.headerRef.current) {
      this.setHeaderHeight(this.headerRef.current.getBoundingClientRect().height);
    }
  }

  componentWillUnmount() {
    this.props.workViewRegistration.unregisterWorkViewCreateItemCallback(this.props.viewId);
  }

  setHeaderHeight(newHeight) {
    if (this.state.headerHeight !== newHeight) {
      this.setState({ headerHeight: newHeight });
    }
  }

  onCreateItem(eventOrTypeId) {
    const typeId = _.isString(eventOrTypeId) ? eventOrTypeId : this.props.defaultType;
    if (!typeSelectors.getTypeIsAllowedInSequence(this.props.localStore, typeId)) {
      return false;
    }
    this.props.onCreateItemInSequence(typeId, null, null, null, this.props.viewId);
    return true;
  }

  copyLocalStore(props) {
    if (props.containerRect.width) {
      this.setState({
        data: getViewData(props.localStore, props.viewId, props.containerRect.width, props.screenContext.isEditMode),
        sortedItemIds: spreadsheetSelectors.getSpreadsheetViewSortedItemIdsForMobile(
          props.localStore,
          props.viewId,
          props.containerRect.width,
          props.screenContext.isEditMode
        )
      });
    }
  }

  dispatchLocal(action) {
    // Dispatch local needs to apply to the original state each time, so we always apply it to props.localStore instead
    const newStore = timelineReducer(this.props.localStore, action, this.props.localStore);

    const data = getViewData(
      newStore,
      this.props.viewId,
      this.props.containerRect.width,
      this.props.screenContext.isEditMode,
      this.props.fontSize
    );
    this.setState({
      data
    });
  }

  internalDragLeave() {
    this.copyLocalStore(this.props);
  }

  startRowDrag(itemIds) {
    this.setState({
      draggedItemIds: List(itemIds)
    });
  }

  requestCanDropItems(itemIds) {
    if (!itemSelectors.getItemsAreAllowedInSequence(this.props.localStore, itemIds)) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_DATE_ORDER_NOT_ALLOWED
      };
    }
    return { canDrop: true };
  }

  dispatchLastMoveAction() {
    if (this._lastAction) {
      this.props.onDispatchAction(this._lastAction);
    }
    this.setState({
      draggedItemIds: List()
    });
    this._lastAction = null;
  }

  onMoveRow(dragItem, dropRow, dropAction) {
    const { localStore } = this.props;
    const dragItemIds = dragItem.multipleItemIds;
    const dropTargetId = dropRow.itemDisplay.id;
    const action = dropSelectors.getActionForSpreadsheetDrop(localStore, dragItemIds, dropTargetId, dropAction);
    if (action) {
      this.props.onDispatchAction(action);
    }
  }

  onCanItemDropOnRow(dragItem, dropRow) {
    const childType = dragItem.itemType;
    const superType = dropRow.type;
    return typeSelectors.canTypeHaveSuperType(this.props.allTypesById, childType, superType);
  }

  onToggleChildren(id, isExpanded) {
    this.props.onToggleChildren(this.props.viewId, id, isExpanded);
  }

  onViewClick(e) {
    const clickWasItem = hasAncestorOfClass(e.target, "itemCardRow") || hasAncestorOfClass(e.target, "eventCol");
    if (!clickWasItem && this.props.selectedItemIds.size > 0) {
      this.props.onViewClick();
    }
  }

  canTypeHaveSuperType(childType, superType) {
    return typeSelectors.canTypeHaveSuperType(this.props.allTypesById, childType, superType);
  }

  requestViewBoundsForCardView(_zoom) {
    return immutableRecords.FlatRectRecord({
      left: 0,
      width: this.props.containerRect.width,
      right: this.props.containerRect.width,
      top: 0,
      height: this.state.data.height,
      bottom: this.state.data.height
    });
  }

  getHeaderOptionGroups() {
    return List([
      {
        title: "Add New",
        id: "addNew",
        options: this.props.orderedSequenceTypes,
        showTitle: false,
        showSearch: false,
        onSelectOption: this.onCreateItem
      }
    ]);
  }

  getHeaderAddButton() {
    const optionGroups = this.getHeaderOptionGroups();
    return isReadonly ? null : (
      <SplitButton
        label="Create"
        id="addEntity"
        optionGroups={optionGroups}
        small={true}
        icon="sidebarAdd"
        onClick={this.onCreateItem}
        backText="Timeline"
      />
    );
  }

  renderHeader() {
    const addButton = this.getHeaderAddButton();
    return (
      <ItemCardListHeader>
        <div className="entityListTitle">
          <Icon icon="inspectorTime" color="mono" />
          <span>{ITEM_CARD_LIST_SEQUENCE_HEADER_TITLE}</span>
        </div>
        {addButton}
      </ItemCardListHeader>
    );
  }

  render() {
    const {
      fontSize,
      viewId,
      isActiveView,
      selectedItemIds,
      itemCardViewSettings,
      onSelectItems,
      onAddToSelection,
      onRemoveFromSelection,
      containerRect,
      isGlobalFilterActive
    } = this.props;
    if (this.state.data.rows.count() === 0) {
      const typeValue = this.props.allTypesById.get(this.props.defaultType);
      let emptyState;
      //#!if readonly === true
      emptyState = (
        <EmptyState type="text" svg={false} fixed={false}>
          <div key="content">
            <p>No visible items to display.</p>
          </div>
        </EmptyState>
      );
      //#!else
// emptyState = (
// <Fragment>
// {this.renderHeader()}
// <EmptyState svg={`default`} type="sidePanel">
// <div key="content">
// {!isGlobalFilterActive ? (
// <p>Looks like you haven&apos;t added any {typeValue.pluralLabel} to your timeline yet.</p>
// ) : (
// <p>No items to display for this filter</p>
// )}
// <FormButton primary={true} label={`Create New ${typeValue.label}`} onClick={this.onCreateItem} />
// </div>
// </EmptyState>
// </Fragment>
// );
      //#!endif
      return emptyState;
    } else {
      const containerContextValue = getItemCardListContainerContextValue(containerRect, fontSize);
      return (
        <Fragment>
          {this.renderHeader()}
          <ContainerContext.Provider value={containerContextValue}>
            <ItemCardListView
              fontSize={fontSize}
              data={this.state.data}
              viewId={viewId}
              isActiveView={isActiveView}
              isNarrativeView={false}
              sortedItemIds={this.state.sortedItemIds}
              selectedItemIds={selectedItemIds}
              itemCardViewSettings={itemCardViewSettings}
              onSelectItems={onSelectItems}
              onAddToSelection={onAddToSelection}
              onRemoveFromSelection={onRemoveFromSelection}
              onToggleChildren={this.onToggleChildren}
              requestViewBoundsForZoom={this.requestViewBoundsForCardView}
              onViewClick={this.onViewClick}
              allowRowDrag={this.props.screenContext.isEditMode}
              allowRowDragNesting={true}
              draggedRowIds={this.state.draggedItemIds}
              onStartRowDrag={this.startRowDrag}
              onRowDrop={this.onMoveRow}
              canItemDropOnRow={this.onCanItemDropOnRow}
              canTypeHaveSuperType={this.canTypeHaveSuperType}
              requestCanDropItems={this.requestCanDropItems}
              isEditMode={this.props.screenContext.isEditMode}
              typesById={this.props.allTypesById}
            />
          </ContainerContext.Provider>
        </Fragment>
      );
    }
  }
}

const mapStateToProps = (state, props) => {
  return {
    localStore: state.present,
    defaultType: spreadsheetSelectors.getSpreadsheetViewDefaultType(state.present, props.viewId),
    calendar: calendarSelectors.getCalendar(state.present),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    allTypesById: definitionSelectors.getTypesById(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    isActiveView: appStateSelectors.isViewActive(state.present, props.viewId),
    itemCardViewSettings: definitionSelectors.getMobileItemCardViewSettings(state.present),
    orderedSequenceTypes: typeSelectors.getOrderedSequenceTypes(state.present),
    isGlobalFilterActive: filterSelectors.isFilterWithIdActive(state.present, GLOBAL_FILTER_ID)
  };
};

const readonlyActions = {
  onSelectItems: itemIds => {
    if (itemIds) {
      let action;
      //#!if harvard === true
      action = batchUtils.batchWithAppendedActions(
        appStateOperations.selectItems(itemIds),
        uiStateOperations.showInspectorModal()
      );
      //#!else
// action = appStateOperations.selectItems(itemIds);
      //#!endif
      return action;
    } else {
      return appStateOperations.clearSelection();
    }
  },
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onToggleChildren: appStateOperations.toggleExpandSuperItemInMobileList,
  onViewClick: appStateOperations.clearSelection
};

const editingActions = {
  onCreateItemInSequence: (typeId, superId, sequenceIndex, label, viewId) => {
    let action = null;
    if (_.isNil(sequenceIndex)) {
      action = batchOperations.createItemWithLabel(typeId, label);
    } else {
      action = batchOperations.createItemInSequenceWithSuperAndLabel(typeId, superId, sequenceIndex, label);
    }
    return batchOperations.matchFiltersAfterCreateAction(action, viewId);
  },
  onDispatchAction: a => {
    return a;
  }
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

MobileSpreadsheetView.propTypes = {
  viewId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  localStore: PropTypes.object.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  defaultType: PropTypes.string.isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onToggleChildren: PropTypes.func.isRequired,
  onViewClick: PropTypes.func.isRequired,
  fontSize: PropTypes.number.isRequired,
  onCreateItemInSequence: PropTypes.func.isRequired,
  onDispatchAction: PropTypes.func.isRequired,
  allTypesById: ImmutablePropTypes.map.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  itemCardViewSettings: PropTypeShapes.itemCardViewSettings.isRequired,
  orderedSequenceTypes: ImmutablePropTypes.list.isRequired,
  showDragHandles: PropTypes.bool,
  isGlobalFilterActive: PropTypes.bool,

  // Workspace context
  workViewRegistration: PropTypes.object.isRequired,

  // ContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  // From withScreenContext
  screenContext: PropTypes.object
};

MobileSpreadsheetView.defaultProps = {
  showDragHandles: true
};

export default withScreenContext(
  withWorkspaceContext(withContainerContext(connect(mapStateToProps, mapDispatchToProps)(MobileSpreadsheetView)))
);
